import type { Commands } from "@/api/commands";
import type { ParamSpec } from "@/api/types";
import { normalizeConfigurableParams } from "@/lib/configurable-params";

function parsePositiveNumber(value: unknown): number | undefined {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n;
}

export function findLeverageSpec(specs: ParamSpec[]): ParamSpec | undefined {
  return specs.find((s) => s.name.toLowerCase() === "leverage");
}

/** Read `leverage = input.float(12.0, ...)` / `input.int(...)` from Pine source. */
export function parseLeverageFromSource(source: string): number | undefined {
  const m = String(source).match(/\bleverage\s*=\s*input\.(?:float|int)\(\s*([0-9]*\.?[0-9]+)/i);
  if (!m) return undefined;
  return parsePositiveNumber(m[1]);
}

export function parsePineStrategyProps(source: string): {
  initial?: number;
  fee?: number;
  margin_long?: number;
  margin_short?: number;
} {
  const initial = String(source).match(/\binitial_capital\s*=\s*([0-9]+(?:\.[0-9]+)?)/);
  const fee = String(source).match(/\bcommission_value\s*=\s*([0-9]+(?:\.[0-9]+)?)/);
  const marginLong = String(source).match(/\bmargin_long\s*=\s*([0-9]+(?:\.[0-9]+)?)/);
  const marginShort = String(source).match(/\bmargin_short\s*=\s*([0-9]+(?:\.[0-9]+)?)/);
  const parseNonNeg = (raw?: string) => {
    if (raw == null) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  };
  return {
    initial: initial ? parsePositiveNumber(initial[1]) : undefined,
    fee: fee ? Number(fee[1]) : undefined,
    margin_long: parseNonNeg(marginLong?.[1]),
    margin_short: parseNonNeg(marginShort?.[1]),
  };
}

export function scriptDeclaresLeverage(specs: ParamSpec[], source?: string): boolean {
  if (findLeverageSpec(specs)) return true;
  return parseLeverageFromSource(source ?? "") !== undefined;
}

export function resolveScriptLeverage(
  specs: ParamSpec[],
  strategyConfig: Record<string, unknown>,
  source?: string
): number | undefined {
  const fromConfig = parsePositiveNumber(strategyConfig.leverage);
  if (fromConfig !== undefined) return fromConfig;
  const spec = findLeverageSpec(specs);
  const fromSpec = parsePositiveNumber(spec?.default);
  if (fromSpec !== undefined) return fromSpec;
  return parseLeverageFromSource(String(source ?? strategyConfig.source ?? ""));
}

/** Extra used to inject integer 5 on every futures save, clobbering Pine defaults. */
export function stripExtraLeverageClobber(config: Record<string, unknown>): Record<string, unknown> {
  const pineLev = parseLeverageFromSource(String(config.source ?? ""));
  const saved = parsePositiveNumber(config.leverage);
  if (pineLev !== undefined && saved === 5 && pineLev !== 5) {
    const next = { ...config };
    delete next.leverage;
    return next;
  }
  return config;
}

export function applyScriptLeverageDefault(
  specs: ParamSpec[],
  config: Record<string, unknown>
): Record<string, unknown> {
  const stripped = stripExtraLeverageClobber(config);
  const spec = findLeverageSpec(specs);
  const def = parsePositiveNumber(spec?.default) ?? parseLeverageFromSource(String(stripped.source ?? ""));
  if (def === undefined) return stripped;
  const saved = parsePositiveNumber(stripped.leverage);
  if (saved === undefined) return stripped;
  return stripped;
}

export function sameParamValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a == b;
  if (typeof a === "boolean" || typeof b === "boolean") {
    const truthy = (v: unknown) => v === true || v === "true";
    const falsy = (v: unknown) => v === false || v === "false";
    return (truthy(a) && truthy(b)) || (falsy(a) && falsy(b));
  }
  const na = Number(a);
  const nb = Number(b);
  if (
    Number.isFinite(na) &&
    Number.isFinite(nb) &&
    String(a).trim() !== "" &&
    String(b).trim() !== "" &&
    !/^(true|false)$/i.test(String(a).trim()) &&
    !/^(true|false)$/i.test(String(b).trim())
  ) {
    return na === nb;
  }
  return String(a) === String(b);
}

/**
 * CLI only passes `source` and lets Pine defaults apply.
 * Keep source plus inputs the user actually changed from the parsed defaults.
 */
export function buildPineStrategyConfig(
  values: Record<string, unknown>,
  specs: ParamSpec[]
): Record<string, unknown> {
  const cfg = stripExtraLeverageClobber({ ...values });
  // Engine margin_* is separate; only drop strategy `leverage` when the Pine has no such input.
  if (!scriptDeclaresLeverage(specs, String(cfg.source ?? ""))) {
    delete cfg.leverage;
  }

  const out: Record<string, unknown> = {};
  if (cfg.source != null && cfg.source !== "") out.source = cfg.source;

  for (const spec of specs) {
    if (spec.name === "source" || spec.type === "script") continue;
    const v = cfg[spec.name];
    if (v === undefined || v === null || v === "") continue;
    if (spec.default !== undefined && spec.default !== null && sameParamValue(v, spec.default)) continue;
    out[spec.name] = v;
  }

  return out;
}

/**
 * UI save path: backend replaces strategy_config entirely, so persist every
 * non-empty input value from the form (do not strip values that match defaults).
 *
 * Always keeps `source` when present on `values`. Callers that run values through
 * `buildConfigPayload` first must re-merge `source` — empty/mismatched specs
 * otherwise drop the script field and backtests fail with
 * `script "source" is required`.
 */
export function buildFormStrategyConfig(
  values: Record<string, unknown>,
  specs: ParamSpec[]
): Record<string, unknown> {
  const cfg = stripExtraLeverageClobber({ ...values });
  // Engine margin_* is separate; only drop strategy `leverage` when the Pine has no such input.
  if (!scriptDeclaresLeverage(specs, String(cfg.source ?? ""))) {
    delete cfg.leverage;
  }

  const out: Record<string, unknown> = {};
  if (cfg.source != null && cfg.source !== "") out.source = cfg.source;

  const inputSpecs = specs.filter((s) => s.name !== "source" && s.type !== "script");
  if (inputSpecs.length > 0) {
    for (const spec of inputSpecs) {
      const v = cfg[spec.name];
      if (v === undefined || v === null || v === "") continue;
      out[spec.name] = v;
    }
    return out;
  }

  // Specs not loaded yet — still persist overrides the user edited.
  for (const [k, v] of Object.entries(cfg)) {
    if (k === "source") continue;
    if (v === undefined || v === null || v === "") continue;
    out[k] = v;
  }
  return out;
}

/** Fetch Pine input specs for the current script source. */
export async function fetchPineStrategySpecs(
  request: Pick<Commands, "getParams">,
  source: string
): Promise<ParamSpec[]> {
  const p = await request.getParams({
    category: "strategy",
    name: "pinescript",
    config: source ? { source } : {},
  });
  return normalizeConfigurableParams(p).specs;
}

const MARGIN_ENGINE_KEYS = new Set([
  "initial",
  "fee",
  "qty",
  "qty_unit",
  "fee_unit",
  "slippage",
  "process_orders_on_close",
  "compounding",
  "pyramiding",
  "margin_long",
  "margin_short",
  "step_size",
  "intrabar_mode",
  "trailing_exec_mode",
]);
/** Removed engine params (replaced by margin_long / margin_short). */
const DROPPED_ENGINE_KEYS = new Set([
  "block_orders_when_equity_lte_zero",
  "validate",
  "leverage",
]);

/** Map legacy engine `leverage` to margin % (same as store migration 018). */
function marginFromLegacyLeverage(raw: unknown): number | undefined {
  if (raw === undefined || raw === null || raw === "") return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return undefined;
  if (n === 0) return 0;
  return 100 / n;
}

export function sanitizeEngineConfig(
  config: Record<string, unknown>,
  _engineProvider: string,
  specs?: ParamSpec[]
): Record<string, unknown> {
  const allowed =
    specs && specs.length > 0
      ? new Set(specs.map((s) => s.name))
      : MARGIN_ENGINE_KEYS;
  const legacyMargin = marginFromLegacyLeverage(config.leverage);
  const out: Record<string, unknown> = {};
  // Backtests saved with the removed "spot" engine kept their capital in `fiat`.
  if (config.fiat !== undefined && config.initial === undefined && allowed.has("initial")) out.initial = config.fiat;
  for (const [k, v] of Object.entries(config)) {
    if (DROPPED_ENGINE_KEYS.has(k)) continue;
    if (!allowed.has(k)) continue;
    out[k] = v;
  }
  // Preserve explicit legacy leverage intent when margins were not already set.
  if (legacyMargin !== undefined) {
    if (out.margin_long === undefined && allowed.has("margin_long")) out.margin_long = legacyMargin;
    if (out.margin_short === undefined && allowed.has("margin_short")) out.margin_short = legacyMargin;
  }
  return out;
}

/**
 * Build engine_config for backtest.create/update.
 * When specs failed to load, buildConfigPayload would return {} and wipe the DB —
 * fall back to the form state filtered by the provider allowlist instead.
 */
export function buildEngineSaveConfig(
  engineConfig: Record<string, unknown>,
  engineSpecs: ParamSpec[],
  engineProvider: string,
  buildPayload: (values: Record<string, unknown>, specs: ParamSpec[]) => Record<string, unknown>,
  fillDefaults: (values: Record<string, unknown>, specs: ParamSpec[]) => Record<string, unknown>
): Record<string, unknown> {
  const filled = fillDefaults(engineConfig, engineSpecs);
  if (engineSpecs.length === 0) {
    return sanitizeEngineConfig(filled, engineProvider);
  }
  return sanitizeEngineConfig(buildPayload(filled, engineSpecs), engineProvider, engineSpecs);
}

const MARGIN_INITIAL_DEFAULTS = new Set([1000, 10000]);
/** Common engine fee defaults (margin 0.04, older UI 0.1) — only replaced when seeding a new form. */
const ENGINE_FEE_DEFAULTS = new Set([0.04, 0.1]);

export function seedEngineFromPine(
  engineConfig: Record<string, unknown>,
  source: string,
  opts?: { replaceFeeDefaults?: boolean; replaceInitialDefaults?: boolean }
): Record<string, unknown> {
  const props = parsePineStrategyProps(source);
  const next = { ...engineConfig };
  const curInitial = Number(next.initial);
  const initialMissing = !Number.isFinite(curInitial);
  const initialIsEngineDefault =
    opts?.replaceInitialDefaults === true &&
    Number.isFinite(curInitial) &&
    MARGIN_INITIAL_DEFAULTS.has(curInitial);
  if (props.initial != null && (initialMissing || initialIsEngineDefault)) {
    next.initial = props.initial;
  }
  const curFee = Number(next.fee);
  const feeMissing =
    next.fee === undefined || next.fee === null || next.fee === "" || !Number.isFinite(curFee);
  const feeIsEngineDefault =
    opts?.replaceFeeDefaults === true && Number.isFinite(curFee) && ENGINE_FEE_DEFAULTS.has(curFee);
  if (props.fee != null && (feeMissing || feeIsEngineDefault)) {
    next.fee = props.fee;
  }
  // Do not copy strategy() margin_* into the engine. Lab UI defaults are
  // margin_long/short = 0 (no gating); Pine leverage is sizing only.
  return next;
}

export function alignStoredBacktestConfig(bt: {
  strategy_config?: Record<string, unknown>;
  engine_config?: Record<string, unknown>;
  engine_provider?: string;
}): {
  strategy_config: Record<string, unknown>;
  engine_config: Record<string, unknown>;
} {
  const strategy_config = stripExtraLeverageClobber({ ...(bt.strategy_config || {}) });
  // Do not replace an explicitly saved fee with Pine commission_value.
  const engine_config = seedEngineFromPine(
    sanitizeEngineConfig(
      { ...(bt.engine_config || {}) },
      "margin"
    ),
    String(strategy_config.source || ""),
    { replaceFeeDefaults: false }
  );
  return { strategy_config, engine_config };
}
